import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { FunctionComponent } from 'react';

import { window } from '../../../../../globals';
import { useIsFeatureEnabled } from '../../../../../helpers/features';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import { REPORTS_V2 } from '../../../../../../shared/featureFlags/flagNames';
import {
  defaultFixPlanOptions,
  defaultIncludeOptions,
  ntiaflatIncludeValues,
  ntiaLicenseValues,
  ntiaMetadataCheckboxValues,
} from '../shared/constants';
import { DefaultDependencyInfoOptions } from '../utils';
import type { IAsync, Project, Revision } from '../../../../../types';
import type { DependencyInfoOption, ReportExclusionOptions } from '../../shared/types';
import type { FormatName } from '../shared/types';

import { Provider } from './ReportContainerContext';

type Props = {
  revision?: IAsync<Revision>;
  project?: IAsync<Project>;
};

const ProjectReportContainer: FunctionComponent<Props> = ({
  revision: revisionAsync,
  project: projectAsync,
  children,
}) => {
  const dispatch = useDispatch();
  const isReportsV2Enabled = useIsFeatureEnabled(REPORTS_V2);

  const selectedDependencyInfoOptions = !_.isEmpty(projectAsync?.data?.bom_column_settings)
    ? (projectAsync?.data?.bom_column_settings as DependencyInfoOption[])
    : DefaultDependencyInfoOptions;
  const [dependencyInfoOptions, setDependencyInfoOptions] = useState(selectedDependencyInfoOptions);

  // Initialize all the state variables that were moved from ReportGenerationV2
  const [format, setFormat] = useState<FormatName | null>(null);
  const [includeOptions, setIncludeOptions] = useState([
    ...defaultIncludeOptions,
    ...ntiaflatIncludeValues,
    ...ntiaLicenseValues,
  ]);
  const [dependencyMetadataOptions, setDependencyMetadataOptions] = useState([
    ...selectedDependencyInfoOptions,
    ...ntiaMetadataCheckboxValues,
  ]);
  const [excludeOptions, setExcludeOptions] = useState<ReportExclusionOptions>({});
  const [fixPlanOptions, setFixPlanOptions] = useState([...defaultFixPlanOptions]);
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState(window.user?.email || '');
  const [supplier, setSupplier] = useState(window.user?.organization?.title || '');
  const [useReportsV2Toggle, setUseReportsV2Toggle] = useState(isReportsV2Enabled);
  const reportCustomText = projectAsync?.data?.reportCustomText || '';
  const revision = revisionAsync?.data?.locator || '';

  useEffect(() => {
    dispatch(ReportGenerationStore.actions.setProjectContext({ projectType: 'project', revision }));
    dispatch(ReportGenerationStore.actions.setCustomHeaderText(reportCustomText));
    dispatch(ReportGenerationStore.actions.setDependencyInfoOptions(selectedDependencyInfoOptions));
  }, [dispatch, revision, reportCustomText, selectedDependencyInfoOptions]);

  return (
    <Provider value={{
      dependencyInfoOptions,
      setDependencyInfoOptions,
      format,
      setFormat,
      includeOptions,
      setIncludeOptions,
      dependencyMetadataOptions,
      setDependencyMetadataOptions,
      excludeOptions,
      setExcludeOptions,
      fixPlanOptions,
      setFixPlanOptions,
      authorName,
      setAuthorName,
      authorEmail,
      setAuthorEmail,
      supplier,
      setSupplier,
      useReportsV2Toggle,
      setUseReportsV2Toggle,
    }}>
      <div>{children}</div>
    </Provider>
  );
};

export default ProjectReportContainer;
